import { useNinetailed } from '@ninetailed/experience.js-next';
import cn from 'classnames';
import FunnelHeader from 'components/funnel/header';
import { useAuth } from 'contexts/auth';
import { useCommon } from 'contexts/common';
import { useDom } from 'contexts/dom';
import useIsClient from 'hooks/common/use-is-client';
import useMobile from 'hooks/common/use-mobile';
import { toOldPetType } from 'utils/pet-type/to-old-pet-type';
import { AiWeightAnalyzerOverlay } from 'modules/ai-weight-analyzer/overlay';
import PickyCatsFloatingButton from 'modules/picky-cats/components/floating-button';
import PromoBar from 'modules/header/promo-bar';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useRootStore } from 'stores/use-root-store';
import { stripRegionPathPrefix } from 'utils/routing/strip-region-path-prefix';
import Footer from '../footer';
import Header from '../header';
import { PetType } from 'constants/pet-type';
import { getPetTypeFromText } from 'utils/pet-type/get-pet-type-from-text';
import { useConfig } from 'config/use-config';
import { stripLocalePathPrefix } from 'utils/i18n/strip-locale-path-prefix';

const Layout: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const pathname = stripRegionPathPrefix(router.pathname);
  const isMobile = useMobile();
  const { commonPagePaths, region } = useConfig();
  const isHome = pathname === '/';
  const isCheckout = pathname.includes('checkout');
  const isFunnel = pathname.includes('futterabo');
  const isFube = pathname.includes('futterberater');
  const isRefundPage = pathname.includes('payment-refund');
  const isSpecialOffer = pathname.includes('special-offer');

  const isCartMobile = pathname.includes('/cart') && isMobile;
  const isCheckoutMobile = pathname.includes('checkout') && isMobile;
  const isResetPasswordMobile =
    pathname.includes('reset-password') && !!router.query.callbackUrl && isMobile;

  const isCatLandingPage =
    stripLocalePathPrefix(router.asPath).split(/[?#]/)[0] === commonPagePaths.catsHome ||
    pathname === commonPagePaths.catsHome;
  const isCollectionPage = pathname.startsWith('/collections/');
  const isProductPage = pathname.startsWith('/products/');
  const pickyCatsLocation = isCatLandingPage
    ? 'cats'
    : isCollectionPage
      ? 'collections'
      : 'products';
  // URL, not the petType store: on these getStaticProps routes the store lags a render behind.
  const pagePetType = getPetTypeFromText({ text: router.asPath, region });
  const hasPickyCatsFloating =
    isMobile &&
    !isHome &&
    pagePetType !== PetType.Dog &&
    (isCatLandingPage || isCollectionPage || isProductPage);
  const { isClient } = useIsClient();
  const { identify } = useNinetailed();
  const { customer } = useAuth();
  const petType = useRootStore((store) => store.petType);
  const { disableScrollHide } = useDom();
  const { mainPromoBar } = useCommon();

  const showHeader =
    isClient &&
    !isCheckout &&
    !isFunnel &&
    !isRefundPage &&
    !isResetPasswordMobile &&
    !isSpecialOffer;

  const promoBarRef = useRef<HTMLDivElement>(null);
  const hasCountdown = Boolean(mainPromoBar?.fields?.countdownDurationSeconds);

  useEffect(() => {
    if (!petType) return;
    identify(`${customer ? customer.customerId : ''}`, {
      petType: toOldPetType(petType),
    });
  }, [customer, identify, petType]);

  // Writes --promo-bar-height so m.header can offset its top position (double-rAF avoids layout thrashing)
  useEffect(() => {
    const el = promoBarRef.current;
    if (!el) return;

    let animationFrameId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.style.setProperty('--promo-bar-height', `${el.offsetHeight}px`);
        });
      });
    });

    resizeObserver.observe(el);

    // Set immediately so --promo-bar-height is ready before first paint
    document.documentElement.style.setProperty('--promo-bar-height', `${el.offsetHeight}px`);

    return () => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      document.documentElement.style.setProperty('--promo-bar-height', '0px');
    };
  }, [hasCountdown]);

  return (
    <div className={cn('flex min-h-screen flex-col')}>
      {showHeader && hasCountdown && mainPromoBar && (
        <div ref={promoBarRef} className="z-navigation-tooltip sticky top-0">
          <PromoBar />
        </div>
      )}
      {showHeader && (
        <Header disableScrollHide={isHome || disableScrollHide} hidePromoBar={hasCountdown} />
      )}
      {isResetPasswordMobile && (
        <FunnelHeader
          onLogoClick={() => {
            router.push('/');
          }}
        />
      )}
      {children}
      <AiWeightAnalyzerOverlay />
      {isClient && hasPickyCatsFloating && <PickyCatsFloatingButton location={pickyCatsLocation} />}
      {isClient && !isFunnel && !isRefundPage && !isFube && !isSpecialOffer && (
        <Footer
          isCheckout={isCheckout}
          hideFooter={isCartMobile || isCheckoutMobile || isResetPasswordMobile}
        />
      )}
    </div>
  );
};
export default Layout;
